import { useRef } from "react";
import { useQueryClient } from "react-query";
import { toast } from "react-toastify";

import axios from "axios";
import { isGeoTiff } from "utils/upload/folderDrop";
import {
  MAX_UPLOAD_BYTES,
  MAX_ZIPS_PER_UPLOAD,
  TOO_MANY_ZIPS_MESSAGE,
  UPLOAD_TOO_LARGE_MESSAGE,
  countZips
} from "utils/upload/uploadKeys";

import { PROJECT_LAYER_LIST_QUERY_KEYS } from "components/project/layers/queries";
import {
  ShapefileUploadPhase,
  ShapefileUploadProgressFile,
  ShapefileUploadState,
  useProjectContext,
  useProjectDispatch
} from "components/project/projects/context";
import {
  ORGANIZATION_SHAPEFILES_QUERY_KEYS,
  PROJECT_SHAPEFILES_QUERY_KEYS
} from "components/project/shapefiles/queries";

import {
  IUploadProjectShapefileChunkResponse,
  deleteUploadSession,
  useUploadProjectGeoTiffMutation
} from "../mutations";
import { useUploadProjectShapefileChunked } from "./useUploadProjectShapefileChunked";

/** Shown when the server refused the drop and said nothing about why. */
export const UPLOAD_FAILED_MESSAGE = `Upload failed. Include all required files together (.shp, .prj, .shx, .dbf), a .zip of them, or a .tif/.tiff file.`;

/** Shown when the upload failed for a reason the files cannot fix. */
export const UPLOAD_RETRY_MESSAGE = `Upload failed. Try again.`;

/** Shown when the drop held a file of zero bytes. */
export const SKIPPED_EMPTY_FILES_MESSAGE = `Empty files cannot be uploaded and were skipped.`;

/** How often the rows are allowed to redraw while one file is going out. */
const PROGRESS_PUBLISH_MS = 250;

/** True when the request was aborted on purpose, rather than failing on its own. */
function isAbortError(error): boolean {
  return (
    axios.isCancel(error) ||
    error?.name === "CanceledError" ||
    error?.name === "AbortError"
  );
}

export function useUploadProjectShapefileList({
  visibility: defaultVisibility,
  projectId: overrideProjectId
}: {
  visibility?: "user" | "organization";
  projectId?: string;
} = {}) {
  const queryClient = useQueryClient();
  const projectDispatch = useProjectDispatch();
  const { selectedProjectId, isUploadingShapefileList } = useProjectContext();
  const uploadProjectShapefileChunked = useUploadProjectShapefileChunked();

  // Aborts the request in flight when the user closes the modal during an upload
  const uploadAbortRef = useRef<AbortController>();
  // The server's name for the upload under way, so a cancel can have it cleaned up
  const uploadIdRef = useRef<string>();
  // True while a drop that also holds shapefiles is on its way. Those are reported
  // on their own, so the GeoTIFFs beside them stay quiet and the drop raises one toast
  const droppedWithShapefilesRef = useRef(false);

  // Use override projectId if provided, otherwise use selectedProjectId from context
  const projectId =
    overrideProjectId !== undefined ? overrideProjectId : selectedProjectId;
  // Use provided visibility or default to "user"
  const visibility = defaultVisibility || "user";

  const uploadProjectGeoTiffMutation = useUploadProjectGeoTiffMutation({
    onSuccess: () => {
      if (droppedWithShapefilesRef.current) return;
      toast.success(`Uploaded successfully.`);
    },
    onError: (error) => {
      // the user cancelled, so the upload stopping is not news
      if (isAbortError(error)) return;
      toast.error(`Upload failed.`);
    },
    onSettled: () => {
      // The GeoTIFFs go out before the shapefiles beside them, so a drop that
      // holds both is not over yet. That one clears the flag when it ends
      if (droppedWithShapefilesRef.current) return;
      projectDispatch({
        payload: {
          isUploadingShapefileList: false
        }
      });
    },
    projectId: projectId
  });

  async function uploadShapefileList({
    files,
    shapefileNodeId
  }: {
    files: File[];
    shapefileNodeId: string;
  }): Promise<{ ok: boolean }> {
    const onlyShapefiles: File[] = [];
    const geoTiffFiles: File[] = [];

    projectDispatch({
      payload: {
        isUploadingShapefileList: true
      }
    });

    // A file of zero bytes has no chunks to send, so the last chunk never arrives
    // and the server imports nothing. The drop zones read a dropped folder and send
    // the files inside it, so this catches a file that really is empty and any
    // folder a browser handed over as a file of zero bytes all the same
    const uploadableFiles = files.filter((file) => file.size > 0);
    if (uploadableFiles.length < files.length) {
      toast.warning(SKIPPED_EMPTY_FILES_MESSAGE);
    }
    if (uploadableFiles.length === 0) {
      projectDispatch({
        payload: {
          isUploadingShapefileList: false
        }
      });
      // nothing was sent and nothing failed: the warning above says what happened
      return { ok: true };
    }

    // The drop modal takes the archives past the limit out of the list before it
    // gets here. The other drop zones send what was dropped straight away, so the
    // whole drop is refused here rather than half imported by the server
    if (countZips(uploadableFiles) > MAX_ZIPS_PER_UPLOAD) {
      toast.error(TOO_MANY_ZIPS_MESSAGE);
      projectDispatch({
        payload: {
          isUploadingShapefileList: false
        }
      });
      return { ok: false };
    }

    // The server counts every file in the upload against one total, and the drop
    // modal cuts a drop down to it. The other drop zones send what was dropped, so
    // the whole drop is refused here
    const droppedBytes = uploadableFiles.reduce((total, file) => total + file.size, 0);
    if (droppedBytes > MAX_UPLOAD_BYTES) {
      toast.error(UPLOAD_TOO_LARGE_MESSAGE);
      projectDispatch({
        payload: {
          isUploadingShapefileList: false
        }
      });
      return { ok: false };
    }

    // One controller for the whole drop: cancel stops the request in flight, and
    // every request after it
    const abortController = new AbortController();
    uploadAbortRef.current = abortController;
    uploadIdRef.current = undefined;

    // Separate shapefiles from GeoTIFFs. The extension is what decides, because the
    // browser leaves file.type empty for a .tif it does not know, and the drop rows
    // count the GeoTIFFs by extension as well. Reading the two differently would show
    // a file as a GeoTIFF and then send it down the shapefile path
    for (const file of uploadableFiles) {
      if (isGeoTiff(file.name)) {
        geoTiffFiles.push(file);
      } else {
        onlyShapefiles.push(file);
      }
    }
    droppedWithShapefilesRef.current = onlyShapefiles.length > 0;

    // One entry per file, in the order they are sent, so the modal can show where
    // the upload has got to. The GeoTIFFs lead, because the request that closes
    // the shapefiles imports the whole upload and has to be the last one
    let progressFiles: ShapefileUploadProgressFile[] = [
      ...geoTiffFiles,
      ...onlyShapefiles
    ].map((file, index) => ({
      // the index keeps two drops of the same file apart
      key: `${index}:${file.name}:${file.size}`,
      name: file.name,
      size: file.size,
      sentBytes: 0,
      state: "waiting"
    }));
    let currentIndex = 0;
    let publishedAt = 0;
    let phase: ShapefileUploadPhase = "uploading";

    function publishProgress() {
      publishedAt = Date.now();
      projectDispatch({
        payload: { uploadProgress: { files: progressFiles, currentIndex, phase } }
      });
    }

    function changeFile(index: number, change: Partial<ShapefileUploadProgressFile>) {
      progressFiles = progressFiles.map((entry, at) =>
        at === index ? { ...entry, ...change } : entry
      );
    }

    function updateFile(index: number, change: Partial<ShapefileUploadProgressFile>) {
      changeFile(index, change);
      publishProgress();
    }

    // Every dispatch redraws each reader of the project context, so the bytes of
    // one file are published on a timer. The state changes around them are not
    // held back, and they carry the newest byte count with them
    function updateSentBytes(index: number, sentBytes: number, size: number) {
      changeFile(index, { sentBytes });
      if (sentBytes >= size || Date.now() - publishedAt >= PROGRESS_PUBLISH_MS) {
        publishProgress();
      }
    }

    // The GeoTIFFs go out in one mutation, so they move from state to state together
    function updateGeoTiffs(state: ShapefileUploadState) {
      progressFiles = progressFiles.map((entry, at) =>
        at < geoTiffFiles.length
          ? {
              ...entry,
              state,
              sentBytes: state === "done" ? entry.size : entry.sentBytes
            }
          : entry
      );
      publishProgress();
    }

    publishProgress();

    // The last chunk's response carries the server's summary of the whole upload
    let finalResult: IUploadProjectShapefileChunkResponse | undefined = undefined;
    // False once the user has been told the drop failed, so the caller can leave
    // the modal open with the files still in it
    let ok = true;

    try {
      // Upload GeoTIFFs. They go first because they are sent and answered one by
      // one, while the request that closes the shapefiles holds the whole import
      let geoTiffsUploaded = false;
      if (geoTiffFiles.length > 0) {
        currentIndex = 0;
        updateGeoTiffs("uploading");

        try {
          // Awaited, so the shapefiles beside them only start once they are in
          await uploadProjectGeoTiffMutation.mutateAsync({
            files: geoTiffFiles,
            visibility,
            projectId,
            shapefileNodeId,
            thickness: "2.0",
            signal: abortController.signal
          });
          updateGeoTiffs("done");
          geoTiffsUploaded = true;
        } catch {
          // The mutation has already told the user, so the rows only say which
          // files it was
          updateGeoTiffs("failed");
          ok = false;
        }
      }

      // Upload shapefiles sequentially with shared uploadId
      if (onlyShapefiles.length > 0) {
        let sharedUploadId: string | undefined = undefined;

        for (let i = 0; i < onlyShapefiles.length; i++) {
          const file = onlyShapefiles[i];
          const isLastFile = i === onlyShapefiles.length - 1;
          // the GeoTIFF rows are above the shapefiles in the list
          const row = geoTiffFiles.length + i;

          currentIndex = row;
          updateFile(row, { state: "uploading" });

          let result: IUploadProjectShapefileChunkResponse | undefined;
          try {
            result = await uploadProjectShapefileChunked.uploadFile({
              file,
              projectId,
              visibility,
              shapefileNodeId,
              thickness: "2.0",
              uploadId: sharedUploadId,
              isLastFile,
              signal: abortController.signal,
              onChunkSent: (sentBytes) => updateSentBytes(row, sentBytes, file.size),
              onUploadId: (id) => {
                uploadIdRef.current = id;
              },
              // The last request unpacks and converts the whole upload, so the rows
              // say so instead of sitting on a full progress bar. It is the last
              // thing the drop sends, GeoTIFFs included, so nothing follows it
              onLastChunkStart: () => {
                phase = "processing";
                publishProgress();
              }
            });
          } catch (error) {
            updateFile(row, { state: "failed" });
            throw error;
          }

          updateFile(row, { state: "done", sentBytes: file.size });

          // Store the uploadId from the first file to use for all subsequent files
          if (!sharedUploadId && result?.uploadId) {
            sharedUploadId = result.uploadId;
          }

          finalResult = result;
        }

        // Invalidate queries after successful shapefile upload
        if (projectId === "organization" || projectId === "") {
          queryClient.invalidateQueries({
            queryKey: [ORGANIZATION_SHAPEFILES_QUERY_KEYS.organizationShapefiles]
          });
        }
        queryClient.invalidateQueries({
          queryKey: [PROJECT_SHAPEFILES_QUERY_KEYS.currentProjectShapefiles, projectId]
        });
        queryClient.invalidateQueries({
          queryKey: [PROJECT_LAYER_LIST_QUERY_KEYS.currentProjectLayerList, projectId]
        });
      }

      // One toast for the whole drop. The server's summary covers the shapefiles
      // only, so the GeoTIFFs beside them are added to the end of it. A GeoTIFF
      // that did not go up has already raised its own error, so it is left out
      if (onlyShapefiles.length > 0) {
        const geoTiffNote = geoTiffsUploaded
          ? ` ${geoTiffFiles.length} ${
              geoTiffFiles.length === 1 ? "GeoTIFF" : "GeoTIFFs"
            } uploaded.`
          : "";
        // A shapefile the server kept carries a reason only when it had to guess
        // the projection. Nothing failed, but a guessed projection can put the
        // layer in the wrong place on the map, so it does not read as a success
        const guessedProjection = finalResult?.results?.some(
          (item) => item.outcome === "created" && !!item.reason
        );
        if (finalResult?.partialFailure) {
          const message = finalResult.message ?? `Some shapefiles could not be imported.`;
          toast.warning(`${message}${geoTiffNote}`);
        } else {
          const message = finalResult?.message ?? `Uploaded successfully.`;
          if (guessedProjection) {
            toast.warning(`${message}${geoTiffNote}`);
          } else {
            toast.success(`${message}${geoTiffNote}`);
          }
        }
      }
    } catch (error) {
      ok = false;
      // The user cancelled, so the modal is already closed and there is nothing
      // to say about a request that was stopped on purpose
      if (isAbortError(error)) {
        return { ok };
      }

      // Only a 4xx says anything the user can act on, and only a 4xx is written
      // for the user. A 5xx body carries the server's internal error text, and a
      // network failure or a bug here has no body at all. Neither of those is the
      // file list's fault, so they ask for another try rather than send the user
      // back to check the files
      const status = error?.response?.status;
      const data = error?.response?.data;
      if (status >= 400 && status < 500) {
        const serverMessage = data?.message ?? data?.error;
        toast.error(
          typeof serverMessage === "string" && serverMessage
            ? serverMessage
            : UPLOAD_FAILED_MESSAGE
        );
      } else {
        toast.error(UPLOAD_RETRY_MESSAGE);
      }
      ok = false;
    } finally {
      // Both branches are awaited above, so the drop is over whichever way it went
      uploadIdRef.current = undefined;
      projectDispatch({ payload: { uploadProgress: null } });
      projectDispatch({
        payload: {
          isUploadingShapefileList: false
        }
      });
    }

    return { ok };
  }

  /** Stops the upload that is on its way. The rows and the flags clear themselves. */
  function cancelUpload() {
    uploadAbortRef.current?.abort();
    // The server is holding the chunks it has already taken, so it is told to drop
    // them rather than leave them behind
    if (uploadIdRef.current) {
      deleteUploadSession(uploadIdRef.current);
    }
  }

  return {
    mutate: uploadShapefileList,
    isLoading: isUploadingShapefileList,
    cancel: cancelUpload
  };
}
